import { Button, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import ReactModal from 'react-modal';
import { getCrmUrl } from '../utils/CrmUtils';
import General from '../utils/GeneralUtils';
import { DiffValueCell } from './ExportReviewGridCell';
import './RecordExplanationSlideout.scss';

// Decision reason structure from API
export interface DecisionReason {
  code: string;
  context?: Record<string, unknown>;
}

export interface FieldMapping {
  source: string;
  target: string | null;
  label: string;
  visible: boolean;
  order: number;
}

export interface RecordExplanationData {
  id_source: string;
  record_type: string;
  data: Record<string, unknown>;
  data_target?: Record<string, unknown>;
  user_decision: string | null;
  operation?: string;
  decision_reason?: DecisionReason;
}

interface RecordExplanationSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  record: RecordExplanationData | null;
  fieldMappings?: FieldMapping[];
  crmName?: string;
  crmType?: 'HUBSPOT' | 'SALESFORCE';
  entityType?: 'CONTACT' | 'INSTITUTION';
  idCrm?: string;
  idCrmCompany?: string;
  hubspotPortalId?: string;
}

/**
 * Get the identifier label based on pkType.
 */
function getIdLabel(context: Record<string, unknown> | undefined): string {
  const pkType = context?.pkType as string;
  if (pkType === 'eduleadId') return 'Edulead ID';
  if (pkType === 'ncesIdSchool') return 'School NCES ID';
  if (pkType === 'ncesIdDistrict') return 'District NCES ID';
  // Fallback for legacy data without pkType
  return context?.eduleadId ? 'Edulead ID' : 'NCES ID';
}

/**
 * Get human-readable messages for a decision reason (plain text for tooltips).
 */
function getReasonMessage(reason: DecisionReason | undefined, crmName: string = 'CRM', entityType?: 'CONTACT' | 'INSTITUTION'): { short: string; long: string } {
  const entityLabel = entityType === 'INSTITUTION' ? 'company' : 'contact';

  if (!reason) {
    return { short: '', long: '' };
  }

  const { code, context } = reason;

  switch (code) {
    case 'MISSING_EMAIL':
      return {
        short: 'No email address',
        long: `This contact has no email address. Email is required by ${crmName} to create or match contacts.`
      };

    case 'INVALID_EMAIL':
      return {
        short: 'Invalid email',
        long: `The email ${context?.email || 'unknown'} was marked as ${context?.status || 'invalid'} by ${context?.provider || 'email validation'}. Based on your export settings, invalid emails are skipped.`
      };

    case 'NOT_IN_CRM':
      return {
        short: `New to ${crmName}`,
        long: context?.email
          ? `No ${entityLabel} with email ${context.email} exists in ${crmName}. A new ${entityLabel} will be created.`
          : `This ${entityLabel} does not exist in ${crmName}. A new ${entityLabel} will be created.`
      };

    case 'FOUND_IN_CRM': {
      const changedFields = (context?.changedFields as string[]) || [];
      const changeCount = changedFields.length;
      const shortMsg = changeCount > 0
        ? `${changeCount} field${changeCount === 1 ? '' : 's'} changing`
        : `Exists in ${crmName}`;
      return {
        short: shortMsg,
        long: context?.email
          ? `A ${entityLabel} with email ${context.email} already exists in ${crmName}. The existing record will be updated.`
          : `This ${entityLabel} already exists in ${crmName}. The existing record will be updated.`
      };
    }

    case 'COMPANY_NOT_IN_CRM': {
      // Determine institution description based on type and access
      const instType = context?.institutionType as string;
      const accessType = context?.accessType as string;
      let institutionDesc: string;
      if (instType === 'DISTRICT') {
        institutionDesc = 'district';
      } else if (instType === 'AGENCY') {
        institutionDesc = 'agency';
      } else if (instType === 'SCHOOL' && accessType === 'PRIVATE') {
        institutionDesc = 'private school';
      } else {
        institutionDesc = 'public school';
      }

      // Get the matching field info
      const requiredMapping = context?.requiredMapping as string || 'identifier';
      const hubspotProperty = context?.hubspotPropertyName as string;
      const idValue = context?.eduleadId || context?.ncesId || 'unknown';

      const companyName = context?.companyName || 'unknown';
      return {
        short: `Company not in ${crmName}`,
        long: `Can't create this company association because the ${institutionDesc} doesn't exist in your ${crmName} account.\n\nFor ${institutionDesc}s, we match on ${requiredMapping}${hubspotProperty ? `, which is mapped to the "${hubspotProperty}" company property in your ${crmName} account` : ''}.\n\nNo company exists in your ${crmName} account where the "${hubspotProperty || 'identifier'}" property equals that of ${companyName}, which is "${idValue}".\n\nThe ${institutionDesc} "${companyName}" needs to be created in ${crmName} first.`
      };
    }

    case 'NO_UNIQUE_IDENTIFIER': {
      const institutionType = (context?.institutionType as string)?.toLowerCase() || 'institution';
      const accessType = context?.accessType as string | undefined;
      // Determine what identifier is expected based on institution type and access
      let expectedIdentifier: string;
      if (context?.institutionType === 'DISTRICT' || context?.institutionType === 'AGENCY') {
        expectedIdentifier = 'District NCES ID';
      } else if (context?.institutionType === 'SCHOOL' && accessType === 'PRIVATE') {
        expectedIdentifier = 'Edulead ID';
      } else {
        expectedIdentifier = 'School NCES ID';
      }
      return {
        short: 'Missing identifier',
        long: `The ${institutionType} "${context?.companyName || 'unknown'}" is missing its ${expectedIdentifier}. This identifier is required to match it with a company in ${crmName}.`
      };
    }

    case 'MAPPING_NOT_CONFIGURED': {
      const requiredMapping = context?.requiredMapping as string || 'identifier';
      return {
        short: 'Mapping not configured',
        long: `The ${requiredMapping} field mapping is not configured. Go to Settings > ${crmName} to map this field to a ${crmName} company property.`
      };
    }

    case 'NOT_ASSOCIATED': {
      // Determine institution description based on type and access
      const instType = context?.institutionType as string;
      const accessType = context?.accessType as string;
      let institutionDesc: string;
      if (instType === 'DISTRICT') {
        institutionDesc = 'district';
      } else if (instType === 'AGENCY') {
        institutionDesc = 'agency';
      } else if (instType === 'SCHOOL' && accessType === 'PRIVATE') {
        institutionDesc = 'private school';
      } else if (instType === 'SCHOOL') {
        institutionDesc = 'public school';
      } else {
        institutionDesc = 'company';
      }

      const requiredMapping = context?.requiredMapping as string;
      const hubspotProperty = context?.hubspotPropertyName as string;
      const idValue = context?.eduleadId || context?.ncesId;
      const companyName = context?.companyName as string;

      // Build the matching details string
      let matchDetails = '';
      if (requiredMapping && hubspotProperty && idValue) {
        matchDetails = ` The ${institutionDesc} was found in ${crmName} by matching ${requiredMapping}, which is mapped to the "${hubspotProperty}" company property, with value "${idValue}".`;
      } else if (requiredMapping && idValue) {
        matchDetails = ` The ${institutionDesc} was found in ${crmName} by matching ${requiredMapping} = "${idValue}".`;
      }

      return {
        short: 'Will associate',
        long: companyName
          ? `This contact is associated with the ${institutionDesc} "${companyName}" in Edulead but not in ${crmName}.${matchDetails} An association will be created.`
          : `This contact is associated with this institution in Edulead but not in ${crmName}. An association will be created.`
      };
    }

    case 'ONLY_IN_CRM':
      return {
        short: 'This contact will be disassociated from this company',
        long: context?.companyName
          ? `This contact is associated with ${context.companyName} in ${crmName} but not in Edulead. The association will be removed.`
          : `This association exists in ${crmName} but not in Edulead. The association will be removed.`
      };

    case 'ALREADY_ASSOCIATED': {
      const companyName = context?.companyName as string;
      return {
        short: 'Already associated',
        long: companyName
          ? `This contact is already associated with "${companyName}" in ${crmName}. No action needed.`
          : `This association already exists in ${crmName}. No action needed.`
      };
    }

    case 'SINGLE_ACCOUNT_ONLY': {
      const companyName = context?.companyName as string;
      const primaryCompanyName = context?.primaryCompanyName as string;
      return {
        short: 'Not linked',
        long: `This export links each contact to one institution only${primaryCompanyName ? `, and ${primaryCompanyName} was chosen` : ''}. ${companyName ? `The association with "${companyName}" is not created` : 'This association is not created'}, and nothing already in ${crmName} is removed. To link both, choose multiple on this export's Options step.`
      };
    }

    case 'NO_CHANGES':
      return {
        short: 'No changes',
        long: `All mapped field values in Edulead match the current values in ${crmName}. No update is needed.`
      };

    case 'CRM_MATCH_COLLISION': {
      const others = ((context?.collidesWith as Array<{ institutionName?: string }>) || [])
        .map(o => o.institutionName || 'another institution');
      return {
        short: `Same ${crmName} company as ${others[0] || 'another institution'}`,
        long: `This institution and ${others.join(', ') || 'another institution'} both match the same company in ${crmName} (ID ${context?.crmId || 'unknown'}). A company cannot be updated from two institutions, and nothing says which one it is, so neither can be exported. Fix the identifier properties on that company in ${crmName}, then retry the review.`
      };
    }

    case 'CRM_MULTIPLE_MATCHES': {
      const matches = (context?.crmMatches as Array<{ crmId: string; name?: string | null }>) || [];
      const idLabel = context?.pkType === 'eduleadId' ? 'Edulead ID' : 'NCES ID';
      return {
        short: `${matches.length || 'Several'} ${crmName} companies with the same ${idLabel}`,
        long: `${matches.length || 'Several'} companies in ${crmName} have the ${idLabel} ${context?.pkValue || ''} of this institution: ${matches.map(m => `${m.name || 'unnamed'} (${m.crmId})`).join(', ')}. There is no way to tell which one it is, so it can't be exported. Merge or fix those companies in ${crmName}, then retry the review.`
      };
    }

    case 'INSTITUTION_INELIGIBLE': {
      const side = context?.side === 'parent' ? 'parent' : 'child';
      const name = (side === 'parent' ? context?.parentName : context?.childName) || `the ${side} institution`;
      return {
        short: `${name} can't be exported`,
        long: `The ${side} institution ${name} can't be exported in this run, so the parent-child association can't be created either.`
      };
    }

    case 'CROSS_OBJECT_DUPLICATE': {
      const crossObjectType = context?.crossObjectType as string || 'record';
      const email = context?.email as string;
      return {
        short: `${crossObjectType} with same email exists`,
        long: email
          ? `A ${crossObjectType} with email ${email} already exists in ${crmName}. The contact cannot be exported because it would create a duplicate.`
          : `A ${crossObjectType} with the same email already exists in ${crmName}. The contact cannot be exported because it would create a duplicate.`
      };
    }

    default:
      return { short: code, long: `Reason: ${code}` };
  }
}

/**
 * Render reason message as JSX with styled highlights.
 */
function renderReasonMessage(reason: DecisionReason | undefined, crmName: string = 'CRM', operation?: string, entityType?: 'CONTACT' | 'INSTITUTION', fieldMappings?: FieldMapping[], crmType?: 'HUBSPOT' | 'SALESFORCE', idCrm?: string): React.ReactNode {
  const entityLabel = entityType === 'INSTITUTION' ? 'company' : 'contact';

  // Build a map of source field → target label for displaying changed field names
  const fieldLabelMap = new Map<string, string>();
  if (fieldMappings) {
    for (const m of fieldMappings) {
      if (m.target && m.visible) {
        fieldLabelMap.set(m.source, m.target);
      }
    }
  }

  if (!reason) {
    // Provide a default explanation based on operation
    if (operation === 'CREATE') {
      return `A new ${entityLabel} will be created in ${crmName}.`;
    } else if (operation === 'UPDATE') {
      return `The existing ${entityLabel} in ${crmName} will be updated with the new values.`;
    } else if (operation === 'SKIP') {
      return `This ${entityLabel} will be skipped and not exported.`;
    }
    return `This ${entityLabel} will be exported to ${crmName}.`;
  }

  const { code, context } = reason;

  switch (code) {
    case 'MISSING_EMAIL':
      return `This contact has no email address. Email is required by ${crmName} to create or match contacts.`;

    case 'INVALID_EMAIL':
      return (
        <>
          The email{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context?.email || 'unknown')}</span>{' '}was marked as {String(context?.status || 'invalid')} by {String(context?.provider || 'email validation')}. Based on your export settings, invalid emails are skipped.
        </>
      );

    case 'NOT_IN_CRM':
      if (context?.email) {
        return (
          <>
            No {entityLabel} with email{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{context.email as string}</span>{' '}exists in {crmName}. A new {entityLabel} will be created.
          </>
        );
      } else if (context?.institutionName) {
        // Institution with NCES/Edulead ID
        const idLabel = getIdLabel(context);
        const idValue = context?.eduleadId || context?.ncesId;
        const hubspotProperty = context?.hubspotPropertyName as string | undefined;
        const requiredMapping = context?.requiredMapping as string | undefined;

        if (idValue) {
          return (
            <>
              <p style={{ marginTop: 0 }}>
                No company with {idLabel}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(idValue)}</span>{' '}exists in {crmName}.
              </p>
              {(requiredMapping || hubspotProperty) && (
                <p>
                  We match on {requiredMapping || idLabel}
                  {hubspotProperty && <>, which is mapped to the <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{hubspotProperty}</span> company property in your {crmName} account</>}.
                </p>
              )}
              <p style={{ marginBottom: 0 }}>
                A new company will be created for{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context.institutionName)}</span>.
              </p>
            </>
          );
        }
        return (
          <>
            The {String(context.institutionType || 'institution').toLowerCase()}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context.institutionName)}</span>{' '}does not exist in {crmName}. A new company will be created.
          </>
        );
      }
      return `This ${entityLabel} does not exist in ${crmName}. A new ${entityLabel} will be created.`;

    case 'FOUND_IN_CRM': {
      const changedFields = (context?.changedFields as string[]) || [];
      const changeCount = changedFields.length;

      // Convert source field names to display labels
      const changedFieldLabels = changedFields
        .map(f => fieldLabelMap.get(f) || f)
        .filter(Boolean);

      // Build the change description
      let changeDescription: React.ReactNode;
      if (changeCount === 0) {
        changeDescription = 'The existing record will be updated.';
      } else if (changedFieldLabels.length > 0) {
        changeDescription = (
          <>
            {changeCount === 1 ? 'The ' : ''}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{changedFieldLabels.join(', ')}</span> field{changeCount === 1 ? '' : 's'} will be updated.
          </>
        );
      } else {
        changeDescription = `${changeCount} field${changeCount === 1 ? '' : 's'} will be updated.`;
      }

      // For Salesforce institution exports, show the Salesforce record ID
      const salesforceNote = (crmType === 'SALESFORCE' && entityType === 'INSTITUTION' && idCrm) ? (
        <div>
          Salesforce record ID: <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{idCrm}</span>
        </div>
      ) : null;

      if (context?.email) {
        return (
          <>
            A {entityLabel} with email{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{context.email as string}</span>{' '}already exists in {crmName}. {changeDescription}
            {salesforceNote}
          </>
        );
      } else if (context?.institutionName) {
        // Institution with NCES/Edulead ID
        const idLabel = getIdLabel(context);
        const idValue = context?.eduleadId || context?.ncesId;
        return idValue ? (
          <>
            <p style={{ marginTop: 0, marginBottom: salesforceNote ? 0 : undefined }}>
              A company with {idLabel}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(idValue)}</span>{' '}already exists in {crmName}. {changeDescription}
            </p>
            {salesforceNote}
          </>
        ) : (
          <>
            <p style={{ marginTop: 0, marginBottom: salesforceNote ? 0 : undefined }}>
              The {String(context.institutionType || 'institution').toLowerCase()}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context.institutionName)}</span>{' '}already exists in {crmName}. {changeDescription}
            </p>
            {salesforceNote}
          </>
        );
      }
      return <>{`This ${entityLabel} already exists in ${crmName}. `}{changeDescription}{salesforceNote}</>;
    }

    case 'COMPANY_NOT_IN_CRM': {
      // Determine institution description based on type and access
      const instType = context?.institutionType as string;
      const accessType = context?.accessType as string;
      let institutionDesc: string;
      if (instType === 'DISTRICT') {
        institutionDesc = 'district';
      } else if (instType === 'AGENCY') {
        institutionDesc = 'agency';
      } else if (instType === 'SCHOOL' && accessType === 'PRIVATE') {
        institutionDesc = 'private school';
      } else {
        institutionDesc = 'public school';
      }

      const requiredMapping = context?.requiredMapping as string || 'identifier';
      const hubspotProperty = context?.hubspotPropertyName as string;
      const idValue = context?.eduleadId || context?.ncesId || 'unknown';

      return (
        <>
          <p style={{ marginTop: 0 }}>
            Can't create this company association because the {institutionDesc} doesn't exist in your {crmName} account.
          </p>
          <p>
            For {institutionDesc}s, we match on {requiredMapping}
            {hubspotProperty && <>, which is mapped to the <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{hubspotProperty}</span> company property in your {crmName} account</>}.
          </p>
          <p>
            No company exists in your {crmName} account where the <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{hubspotProperty || 'identifier'}</span> property equals that of <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context?.companyName || 'unknown')}</span>, which is <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(idValue)}</span>.
          </p>
          <p style={{ marginBottom: 0 }}>
            The {institutionDesc} <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context?.companyName || 'unknown')}</span> needs to be created in {crmName} first.
          </p>
        </>
      );
    }

    case 'NO_UNIQUE_IDENTIFIER': {
      const institutionType = (context?.institutionType as string)?.toLowerCase() || 'institution';
      const accessType = context?.accessType as string | undefined;
      // Determine what identifier is expected based on institution type and access
      let expectedIdentifier: string;
      let explanation: string;
      if (context?.institutionType === 'DISTRICT' || context?.institutionType === 'AGENCY') {
        expectedIdentifier = 'District NCES ID';
        explanation = 'Public districts and agencies are matched using their NCES ID from the National Center for Education Statistics.';
      } else if (context?.institutionType === 'SCHOOL' && accessType === 'PRIVATE') {
        expectedIdentifier = 'Edulead ID';
        explanation = `Private schools are matched using their Edulead ID. Make sure you have configured the Edulead ID field mapping in Settings > ${crmName}.`;
      } else {
        expectedIdentifier = 'School NCES ID';
        explanation = 'Public schools are matched using their NCES ID from the National Center for Education Statistics.';
      }
      return (
        <>
          <p style={{ marginTop: 0 }}>
            The {institutionType}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context?.companyName || 'unknown')}</span>{' '}is missing its <strong>{expectedIdentifier}</strong>.
          </p>
          <p style={{ marginBottom: 0 }}>
            {explanation}
          </p>
        </>
      );
    }

    case 'MAPPING_NOT_CONFIGURED': {
      const requiredMapping = context?.requiredMapping as string || 'identifier';
      return (
        <>
          <p style={{ marginTop: 0 }}>
            The <strong>{requiredMapping}</strong> field mapping is not configured.
          </p>
          <p style={{ marginBottom: 0 }}>
            Go to <strong>Settings &gt; {crmName}</strong> to map this field to a {crmName} company property.
          </p>
        </>
      );
    }

    case 'NOT_ASSOCIATED': {
      // Determine institution description based on type and access
      const instType = context?.institutionType as string;
      const accessType = context?.accessType as string;
      let institutionDesc: string;
      if (instType === 'DISTRICT') {
        institutionDesc = 'district';
      } else if (instType === 'AGENCY') {
        institutionDesc = 'agency';
      } else if (instType === 'SCHOOL' && accessType === 'PRIVATE') {
        institutionDesc = 'private school';
      } else if (instType === 'SCHOOL') {
        institutionDesc = 'public school';
      } else {
        institutionDesc = 'company';
      }

      const requiredMapping = context?.requiredMapping as string;
      const hubspotProperty = context?.hubspotPropertyName as string;
      const idValue = (context?.eduleadId || context?.ncesId) as string | undefined;
      const companyName = context?.companyName as string;

      if (!companyName) {
        return `This contact is associated with this institution in Edulead but not in ${crmName}. An association will be created.`;
      }

      // Only show matching details if we have the required info
      const hasMatchDetails = requiredMapping && idValue;

      return (
        <>
          <p style={{ marginTop: 0 }}>
            This contact is associated with the {institutionDesc}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{companyName}</span>{' '}in Edulead but not in {crmName}.
          </p>
          {hasMatchDetails && (
            <p>
              The {institutionDesc} was found in {crmName} by matching {requiredMapping}
              {hubspotProperty && <>, which is mapped to the <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{hubspotProperty}</span> company property</>}
              , with value <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{idValue}</span>.
            </p>
          )}
          <p style={{ marginBottom: 0 }}>
            An association will be created between this contact and the {institutionDesc} in {crmName}.
          </p>
        </>
      );
    }

    case 'ONLY_IN_CRM':
      return context?.companyName ? (
        <>
          This contact is associated with{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{context.companyName as string}</span>{' '}in {crmName} but not in Edulead. The association will be removed.
        </>
      ) : (
        `This association exists in ${crmName} but not in Edulead. The association will be removed.`
      );

    case 'ALREADY_ASSOCIATED':
      return context?.companyName ? (
        <>
          This contact is already associated with{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{context.companyName as string}</span>{' '}in {crmName}. No action needed.
        </>
      ) : (
        `This association already exists in ${crmName}. No action needed.`
      );

    case 'SINGLE_ACCOUNT_ONLY': {
      const companyName = context?.companyName as string;
      const primaryCompanyName = context?.primaryCompanyName as string;
      return (
        <>
          This export links each contact to one institution only
          {primaryCompanyName && <>, and{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{primaryCompanyName}</span>{' '}was chosen</>}.
          {' '}The association with{' '}
          <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{companyName || 'this company'}</span>{' '}
          is not created, and nothing already in {crmName} is removed.
          {' '}To link both, choose multiple on this export's Options step.
        </>
      );
    }

    case 'NO_CHANGES':
      return `All mapped field values in Edulead match the current values in ${crmName}. No update is needed.`;

    case 'CRM_MATCH_COLLISION': {
      const others = ((context?.collidesWith as Array<{ institutionName?: string; pkType?: string; pkValue?: string }>) || []);
      return (
        <>
          <p style={{ marginTop: 0 }}>
            This institution and{' '}
            {others.map((o, i) => (
              <span key={i}>
                {i > 0 && ', '}
                <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{o.institutionName || 'another institution'}</span>
                {o.pkValue && <> ({o.pkType}: {o.pkValue})</>}
              </span>
            ))}
            {' '}both match the same company in {crmName}
            {context?.crmId ? <> (ID <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context.crmId)}</span>)</> : null}.
          </p>
          <p style={{ marginBottom: 0 }}>
            A company cannot be updated from two institutions, and nothing says which one it is, so neither can be exported. Fix the identifier properties on that company in {crmName}, then retry the review.
          </p>
        </>
      );
    }

    case 'CRM_MULTIPLE_MATCHES': {
      const matches = (context?.crmMatches as Array<{ crmId: string; name?: string | null }>) || [];
      const idLabel = context?.pkType === 'eduleadId' ? 'Edulead ID' : 'NCES ID';
      return (
        <>
          <p style={{ marginTop: 0 }}>
            {matches.length || 'Several'} companies in {crmName} have the {idLabel}{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{String(context?.pkValue || '')}</span>{' '}of this institution:
          </p>
          <ul style={{ marginTop: 0 }}>
            {matches.map(m => (
              <li key={m.crmId}>
                <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{m.name || 'unnamed'}</span> (ID {m.crmId})
              </li>
            ))}
          </ul>
          <p style={{ marginBottom: 0 }}>
            There is no way to tell which one it is, so it can't be exported. Merge or fix those companies in {crmName}, then retry the review.
          </p>
        </>
      );
    }

    case 'INSTITUTION_INELIGIBLE': {
      const side = context?.side === 'parent' ? 'parent' : 'child';
      const name = String((side === 'parent' ? context?.parentName : context?.childName) || `the ${side} institution`);
      const institutionReason = context?.institutionReason as DecisionReason | undefined;
      const why = institutionReason ? getReasonMessage(institutionReason, crmName, 'INSTITUTION').short : '';
      return (
        <>
          The {side} institution{' '}<span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{name}</span>{' '}can't be exported in this run{why ? <> ({why})</> : null}, so the parent-child association can't be created either.
        </>
      );
    }

    case 'CROSS_OBJECT_DUPLICATE': {
      const crossObjectType = context?.crossObjectType as string || 'record';
      const email = context?.email as string;
      const crossObjectId = context?.crossObjectId as string;
      return (
        <>
          A <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{crossObjectType}</span> with email{' '}
          <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{email || 'the same address'}</span>{' '}
          already exists in {crmName}
          {crossObjectId && <> (ID: <span className="desc-13" style={{ marginLeft: 2, marginRight: 2 }}>{crossObjectId}</span>)</>}.
          {' '}This contact cannot be exported as a {crossObjectType === 'Lead' ? 'Contact' : 'Lead'} because Salesforce's duplicate rules prevent creating records with the same email across Contacts and Leads.
          {' '}To resolve this, either convert or delete the existing {crossObjectType} in Salesforce first.
        </>
      );
    }

    default:
      return `Reason: ${code}`;
  }
}

export function RecordExplanationSlideout({ isOpen, onClose, record, fieldMappings, crmName = 'CRM', crmType = 'HUBSPOT', entityType = 'CONTACT', idCrm, idCrmCompany, hubspotPortalId }: RecordExplanationSlideoutProps) {
  if (!record) return null;

  // For INSTITUTION exports, idCrm is the company ID
  // For CONTACT exports, idCrm is the contact ID and idCrmCompany is the associated company
  const crmContactUrl = entityType === 'CONTACT' ? getCrmUrl(crmType, 'contact', idCrm, { hubspotPortalId }) : null;
  const crmCompanyUrl = entityType === 'INSTITUTION' ? getCrmUrl(crmType, 'company', idCrm, { hubspotPortalId }) : getCrmUrl(crmType, 'company', idCrmCompany, { hubspotPortalId });

  const isUpdate = record.operation === 'UPDATE';
  const showComparison = isUpdate && record.data_target && fieldMappings && fieldMappings.length > 0;

  // Build comparison rows for UPDATE contacts
  // Use changedFields from stored decision reason for accurate highlighting
  const changedFields = (record.decision_reason?.context?.changedFields as string[]) || [];
  const comparisonRows = showComparison
    ? fieldMappings
        .filter(m => m.visible)
        .sort((a, b) => a.order - b.order)
        .map(mapping => {
          const eduleadValue = record.data[mapping.source];
          const crmValue = record.data_target![mapping.source];
          return {
            label: mapping.target,
            eduleadValue: eduleadValue != null ? String(eduleadValue) : '',
            crmValue: crmValue != null ? String(crmValue) : '',
            isChanged: changedFields.includes(mapping.source)
          };
        })
    : [];

  // Use wider slideout when showing comparison table
  const slideoutWidth = showComparison ? 'min(90vw, 900px)' : 'min(90vw, 450px)';

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: slideoutWidth })}
      closeTimeoutMS={200}
      parentSelector={General.getModalRoot}
    >
      <div className="record-explanation-slideout">
        <div className="explanation">
          <div className="desc-10" style={{ marginBottom: '10px' }}>Action description</div>
          <p className="explanation-text">{renderReasonMessage(record.decision_reason, crmName, record.operation, entityType, fieldMappings, crmType, idCrm)}</p>
        </div>

        {showComparison && comparisonRows.length > 0 && (
          <div className="comparison">
            <div className="desc-10" style={{ marginBottom: '10px' }}>Field changes</div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell as="th">Field</TableCell>
                  <TableCell as="th">Current {crmName} Value</TableCell>
                  <TableCell as="th">New {crmName} Value</TableCell>
                  <TableCell as="th"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonRows.map(row => (
                  <TableRow key={row.label} className={row.isChanged ? 'row-changed' : ''}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      <DiffValueCell
                        value={row.crmValue}
                        compareValue={row.eduleadValue}
                        type="old"
                        isChanged={row.isChanged}
                      />
                    </TableCell>
                    <TableCell>
                      <DiffValueCell
                        value={row.eduleadValue}
                        compareValue={row.crmValue}
                        type="new"
                        isChanged={row.isChanged}
                      />
                    </TableCell>
                    <TableCell className="status-cell">{!row.isChanged && <span className="desc-15">No change</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {(crmContactUrl || crmCompanyUrl) && (
          <div className="crm-links">
            {crmContactUrl && (
              <Button size="small" className="tiny" as="a" href={crmContactUrl} target="_blank" rel="noopener noreferrer">
                View contact in {crmName}
              </Button>
            )}
            {crmCompanyUrl && (
              <Button size="small" className="tiny" as="a" href={crmCompanyUrl} target="_blank" rel="noopener noreferrer">
                View company in {crmName}
              </Button>
            )}
          </div>
        )}

      </div>
    </ReactModal>
  );
}

// Export the helper function for use in tooltips
export { getReasonMessage };
